import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { answerQuizSession, completeQuizSession, fetchFreshQuestion, startQuizSession } from "../services/quizRuntimeService";

const mapQuestion = (question) => {
  if (!question) return null;
  return {
    ...question,
    correctAnswer: question.correct_answer_index,
  };
};

const mergeFreshState = (prevState, freshState) => {
  if (!prevState || !freshState) return freshState;
  if (freshState.score === 0 && prevState.score > 0) {
    return {
      ...freshState,
      score: prevState.score,
      messages: prevState.messages,
    };
  }
  return freshState;
};

const buildQuestionKey = (question) => {
  if (!question) return "";
  const text = question.question || "";
  const options = Array.isArray(question.options) ? question.options.join("|") : "";
  return `${text}|${options}`;
};

const createClientSessionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getCorrectStorageKey = ({ userId, topicId }) => {
  if (!userId || !topicId) return null;
  return `quiz_correct_count:${userId}:${topicId}`;
};

const readCorrectCount = ({ userId, topicId }) => {
  if (typeof window === "undefined") return 0;
  const key = getCorrectStorageKey({ userId, topicId });
  if (!key) return 0;
  const rawValue = localStorage.getItem(key);
  const parsedValue = Number(rawValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const writeCorrectCount = ({ userId, topicId, value }) => {
  if (typeof window === "undefined") return;
  const key = getCorrectStorageKey({ userId, topicId });
  if (!key) return;
  localStorage.setItem(key, String(value));
};

const getStartQuizErrorMessage = (message) => {
  const normalizedMessage = String(message || "").toLowerCase();

  if (
    normalizedMessage.includes("no hay preguntas disponibles para este tema") ||
    normalizedMessage.includes("este tema aún no tiene preguntas pre-generadas") ||
    normalizedMessage.includes("este tema aun no tiene preguntas pre-generadas") ||
    normalizedMessage.includes("not found") ||
    normalizedMessage.includes("error 404")
  ) {
    return "Ya respondiste todas las preguntas del quiz.";
  }

  return message || "Error iniciando trivia";
};

const isNoMoreQuestionsError = (message) => {
  const normalizedMessage = String(message || "").toLowerCase();

  return (
    normalizedMessage.includes("no hay preguntas disponibles para este tema") ||
    normalizedMessage.includes("este tema aún no tiene preguntas pre-generadas") ||
    normalizedMessage.includes("este tema aun no tiene preguntas pre-generadas") ||
    normalizedMessage.includes("ya has contestado todas correctamente") ||
    normalizedMessage.includes("ya respondiste todas las preguntas del quiz")
  );
};

export const useQuizSession = ({ headers, userId }) => {
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [state, setState] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [correctAnswerIndex, setCorrectAnswerIndex] = useState(null);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [lastResultIsCorrect, setLastResultIsCorrect] = useState(null);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [answerExplanation, setAnswerExplanation] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [isStarting, setIsStarting] = useState(false);
  const [isFetchingNext, setIsFetchingNext] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const [questionTick, setQuestionTick] = useState(0);
  const seenQuestionsRef = useRef(new Set());
  const startLockRef = useRef(false);
  const nextLockRef = useRef(false);
  const timerRef = useRef(null);
  const answeredRef = useRef(false);
  const timeoutSentRef = useRef(false);
  const activeQuestionKeyRef = useRef("");
  const activeQuestionSeqRef = useRef(0);

  const totalQuestions = selectedQuiz?.questionsCount || selectedQuiz?.questions?.length || 10;
  const progress = useMemo(() => {
    if (!totalQuestions) return 0;
    return Math.min(100, (questionNumber / totalQuestions) * 100);
  }, [questionNumber, totalQuestions]);

  const sessionIdRef = useRef(null);

  const startQuiz = useCallback(async (quiz) => {
    if (!quiz?.topicId) {
      setError("Trivia sin topicId configurado");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!userId) {
        throw new Error("userId no disponible para crear sesion");
      }
      const sessionId = createClientSessionId();
      sessionIdRef.current = sessionId;
      console.log("[QUIZ] session:", sessionId);

      const response = await startQuizSession({
        topicId: quiz.topicId,
        sessionId,
        userId,
        reset: Boolean(quiz?.resetProgress),
        headers: {
          ...headers,
        },
      });
      console.log("[QUIZ] start question:", response?.question?.question);

      setSelectedQuiz(quiz);
      setState(response.state);
      const firstQuestion = mapQuestion(response.question || response.state?.current_question);
      setCurrentQuestion(firstQuestion);
      activeQuestionKeyRef.current = buildQuestionKey(firstQuestion);
      activeQuestionSeqRef.current += 1;
      setSelectedAnswer(null);
      setCorrectAnswerIndex(null);
      setHasAnswered(false);
      setTimedOut(false);
      setLastResultIsCorrect(null);
      timeoutSentRef.current = false;
      answeredRef.current = false;
      setScore(response.state?.score || 0);
      setCorrectCount(0);
      setShowResult(false);
      setShowFeedback(false);
      setFeedback("");
      setAnswerExplanation("");
      setQuestionNumber(1);
      setTimeLeft(30);
      setQuestionTick((prev) => prev + 1);
      seenQuestionsRef.current = new Set();
      if (firstQuestion) {
        seenQuestionsRef.current.add(buildQuestionKey(firstQuestion));
      }
      writeCorrectCount({ userId, topicId: quiz.topicId, value: 0 });
    } catch (err) {
      setError(getStartQuizErrorMessage(err?.message));
    } finally {
      setLoading(false);
    }
  }, [headers, userId]);

  const handleStart = useCallback(async (quiz) => {
    if (startLockRef.current || isStarting) return;
    startLockRef.current = true;
    setIsStarting(true);
    try {
      await startQuiz(quiz);
    } finally {
      setTimeout(() => {
        startLockRef.current = false;
        setIsStarting(false);
      }, 700);
    }
  }, [isStarting, startQuiz]);

  const sendAnswer = useCallback(async ({ answerIndex, isTimeout = false }) => {
    if (!state || loading || hasAnswered || answeredRef.current) return;
    answeredRef.current = true;
    const sentQuestionKey = activeQuestionKeyRef.current;
    const sentQuestionSeq = activeQuestionSeqRef.current;

    setLoading(true);
    setError(null);

    try {
      console.log("[QUIZ] answer sent question:", state?.current_question?.question);
      const payloadState = userId ? { ...state, user_id: userId } : state;
      const payloadUserId = userId || state?.user_id;
      console.log("[QUIZ] answer payload", {
        state: payloadState,
        user_answer: answerIndex,
        user_id: payloadUserId,
      });
      const response = await answerQuizSession({
        state: payloadState,
        userAnswer: answerIndex,
        userId: payloadUserId,
        sessionId: sessionIdRef.current,
        headers: {
          ...headers,
        },
      });
      console.log("[QUIZ] answer result:", response?.result);
      if (
        activeQuestionSeqRef.current !== sentQuestionSeq ||
        activeQuestionKeyRef.current !== sentQuestionKey
      ) {
        return;
      }

      const resultPayload = response?.result?.result || response?.result || {};
      const isCorrect = Boolean(resultPayload?.is_correct ?? resultPayload?.isCorrect ?? false);
      const currentExplanation =
        resultPayload?.explanation ||
        response?.question?.explanation ||
        state?.current_question?.explanation ||
        "";

      if (isCorrect) {
        setLastResultIsCorrect(true);
        const topicId = selectedQuiz?.topicId || state?.topic_id;
        const nextCorrectCount = readCorrectCount({ userId, topicId }) + 1;
        setCorrectCount(nextCorrectCount);
        writeCorrectCount({ userId, topicId, value: nextCorrectCount });
      } else {
        setLastResultIsCorrect(false);
      }

      const nextState = response?.next_state || null;
      setState(nextState);
      setScore(response?.result?.score ?? nextState?.score ?? 0);
      if (isCorrect) {
        setFeedback(response?.result?.feedback || "Respuesta correcta.");
        setAnswerExplanation("");
      } else if (isTimeout) {
        setFeedback("Respuesta incorrecta.");
        setAnswerExplanation(currentExplanation);
      } else {
        setFeedback("Respuesta incorrecta.");
        setAnswerExplanation(currentExplanation);
      }
      setShowFeedback(true);
      setHasAnswered(true);
      setCorrectAnswerIndex(isCorrect ? (state?.current_question?.correct_answer_index ?? answerIndex) : null);
    } catch (err) {
      setError(err.message || "Error enviando respuesta");
      setSelectedAnswer(null);
    } finally {
      setLoading(false);
    }
  }, [hasAnswered, headers, loading, selectedQuiz?.topicId, state, userId]);

  const answerQuestion = useCallback(async (answerIndex) => {
    if (hasAnswered || showResult) return;
    setSelectedAnswer(answerIndex);
    await sendAnswer({ answerIndex });
  }, [hasAnswered, showResult, sendAnswer]);

  const submitAnswer = useCallback(async () => {
    if (selectedAnswer === null) return;
    await sendAnswer({ answerIndex: selectedAnswer });
  }, [selectedAnswer, sendAnswer]);

  const submitTimeoutAnswer = useCallback(async () => {
    if (hasAnswered || loading || timeoutSentRef.current) return;
    timeoutSentRef.current = true;
    if (selectedAnswer !== null) {
      setTimedOut(false);
      await sendAnswer({ answerIndex: selectedAnswer, isTimeout: false });
      return;
    }
    setHasAnswered(true);
    const correct = state?.current_question?.correct_answer_index;
    const n = state?.current_question?.options?.length || 0;
    if (!Number.isFinite(correct) || n <= 1) {
      setTimedOut(true);
      setFeedback("Respuesta incorrecta.");
      setAnswerExplanation(state?.current_question?.explanation || "");
      setShowFeedback(true);
      setLastResultIsCorrect(false);
      setCorrectAnswerIndex(null);
      return;
    }
    const forcedWrong = (correct + 1) % n;
    setTimedOut(true);
    setLastResultIsCorrect(false);
    await sendAnswer({ answerIndex: forcedWrong, isTimeout: true });
  }, [hasAnswered, loading, sendAnswer, selectedAnswer, state]);

  const loadNextQuestion = useCallback(async () => {
    if (!state || loading || isFetchingNext || nextLockRef.current) return;

    setLoading(true);
    setError(null);
    nextLockRef.current = true;
    setIsFetchingNext(true);

    try {
      const payload = {
        topic_id: state?.topic_id,
        // TODO: ajustar payload cuando confirmemos el body real
      };
      const response = await fetchFreshQuestion({
        payload,
        userId,
        sessionId: sessionIdRef.current,
        headers: {
          ...headers,
        },
      });
      console.log("[QUIZ] fresh question:", response?.question?.question);
      const mergedState = mergeFreshState(state, response?.state || response?.next_state);
      const nextQuestion = mapQuestion(response?.question || mergedState?.current_question);
      if (!nextQuestion) {
        console.log("[QUIZ] FINISH -> final score:", mergedState?.score ?? score);
        try {
          await completeQuizSession({
            sessionId: sessionIdRef.current,
            userId,
            headers: { ...headers },
          });
        } catch (completeErr) {
          console.warn("[QUIZ] Error completando sesion:", completeErr?.message);
        }
        setState(mergedState);
        setScore(mergedState?.score ?? score);
        setShowResult(true);
        return;
      }
      const nextKey = buildQuestionKey(nextQuestion);
      if (!seenQuestionsRef.current.has(nextKey)) {
        seenQuestionsRef.current.add(nextKey);
      }
      setState(mergedState);
      setScore(mergedState?.score ?? score);
      setCurrentQuestion(nextQuestion);
      activeQuestionKeyRef.current = buildQuestionKey(nextQuestion);
      activeQuestionSeqRef.current += 1;
      setSelectedAnswer(null);
      setCorrectAnswerIndex(null);
      setHasAnswered(false);
      setTimedOut(false);
      setLastResultIsCorrect(null);
      timeoutSentRef.current = false;
      answeredRef.current = false;
      setShowFeedback(false);
      setFeedback("");
      setAnswerExplanation("");
      setShowResult(false);
      setQuestionNumber((prev) => prev + 1);
      setTimeLeft(30);
      setQuestionTick((prev) => prev + 1);
    } catch (err) {
      if (isNoMoreQuestionsError(err?.message)) {
        setError(null);
        try {
          await completeQuizSession({
            sessionId: sessionIdRef.current,
            userId,
            headers: { ...headers },
          });
        } catch (completeErr) {
          console.warn("[QUIZ] Error completando sesion:", completeErr?.message);
        }
        setShowResult(true);
      } else {
        setError(err.message || "Error cargando siguiente pregunta");
      }
    } finally {
      setLoading(false);
      setTimeout(() => {
        nextLockRef.current = false;
        setIsFetchingNext(false);
      }, 700);
    }
  }, [headers, isFetchingNext, loading, questionNumber, score, state, totalQuestions, userId]);

  useEffect(() => {
    if (!currentQuestion || hasAnswered || showResult) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }

    setTimeLeft(30);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [currentQuestion, hasAnswered, questionTick, showResult]);

  useEffect(() => {
    if (timeLeft > 0 || hasAnswered || showResult) return;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    submitTimeoutAnswer();
  }, [hasAnswered, showResult, submitTimeoutAnswer, timeLeft]);

  const resetQuiz = useCallback(() => {
    setSelectedQuiz(null);
    setState(null);
    setCurrentQuestion(null);
    setSelectedAnswer(null);
    setCorrectAnswerIndex(null);
    setHasAnswered(false);
    setTimedOut(false);
    setLastResultIsCorrect(null);
    setScore(0);
    setCorrectCount(0);
    setShowResult(false);
    setShowFeedback(false);
    setFeedback("");
    setAnswerExplanation("");
    setQuestionNumber(1);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    selectedQuiz,
    currentQuestion,
    selectedAnswer,
    showResult,
    score,
    correctCount,
    loading,
    error,
    questionNumber,
    totalQuestions,
    progress,
    handleStart,
    answerQuestion,
    submitAnswer,
    loadNextQuestion,
    resetQuiz,
    clearError,
    showFeedback,
    feedback,
    answerExplanation,
    hasAnswered,
    correctAnswerIndex,
    timedOut,
    lastResultIsCorrect,
    isStarting,
    isFetchingNext,
    timeLeft,
  };
};
